import { StripePaymentMethodListItem } from "@/app/Stripe/StripePaymentMethods/Components/StripePaymentMethodListItem";
import { WarningBanner } from "@/lib/Components/Banners/WarningBanner";
import {
  ContactRecordFieldsFragment,
  StripeCustomerFieldsFragment,
} from "@/gql/graphql";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { createOffSessionSetupIntentMutation } from "@/app/Stripe/StripePaymentMethods/GraphQL/createOffSessionSetupIntentMutation";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { Button } from "@/components/catalyst/button";
import { CreditCardIcon } from "@heroicons/react/24/outline";
import { lazy } from "react";
import type { BillingDetails } from "@/app/Stripe/StripePaymentMethods/types";

const StripeSetupIntentDialog = lazy(
  () =>
    import(
      "@/app/Stripe/StripePaymentMethods/Components/Dialogs/StripeSetupIntentDialog"
    ),
);

function buildBillingDetails(
  contact: ContactRecordFieldsFragment,
): BillingDetails {
  return {
    name: contact.name ?? undefined,
    email: contact.billing_email ?? undefined,
    phone: contact.billing_phone ?? undefined,
    address: contact.billingAddress
      ? {
          line1: contact.billingAddress.address_line_1 ?? undefined,
          line2: contact.billingAddress.address_line_2 ?? undefined,
          city: contact.billingAddress.city ?? undefined,
          state: contact.billingAddress.state ?? undefined,
          postal_code: contact.billingAddress.postcode ?? undefined,
          country: contact.billingAddress.country ?? undefined,
        }
      : undefined,
  };
}

export function AddPaymentMethodButton({
  contact,
}: {
  contact: ContactRecordFieldsFragment & StripeCustomerFieldsFragment;
}) {
  const { mutateAsync } = useGqlMutation(createOffSessionSetupIntentMutation);
  const { open } = useDialog(StripeSetupIntentDialog);

  async function handleAddPaymentMethod() {
    const res = await mutateAsync({
      input: {
        contact_id: contact.id,
      },
    });

    open({
      clientSecret: res.createSetupIntent,
      billingDetails: buildBillingDetails(contact),
      initialPaymentMethodCount:
        contact.stripeCustomer?.paymentMethods?.length ?? 0,
    });
  }

  return <Button onClick={handleAddPaymentMethod}>Add payment method</Button>;
}

export function StripePaymentMethodCard({
  contact,
}: {
  contact: StripeCustomerFieldsFragment & ContactRecordFieldsFragment;
}) {
  const billingContact = contact;

  if (!billingContact) {
    return (
      <WarningBanner>
        This supplier does not have a billing contact. Please add one to manage
        payment methods.
      </WarningBanner>
    );
  }

  const paymentMethods = billingContact.stripeCustomer?.paymentMethods || [];
  const primaryMethod = paymentMethods.find((method) => method.is_primary);
  const otherMethods = paymentMethods.filter((method) => !method.is_primary);

  // Sort with primary first, then others
  const sortedMethods = primaryMethod
    ? [primaryMethod, ...otherMethods]
    : otherMethods;

  return sortedMethods.length > 0 ? (
    <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {sortedMethods.map((method) => {
        return (
          <StripePaymentMethodListItem paymentMethod={method} key={method.id} />
        );
      })}
    </ul>
  ) : (
    <div className="rounded-lg border-2 border-dashed border-gray-200 p-12 text-center">
      <CreditCardIcon className="mx-auto h-12 w-12 text-gray-400" />
      <h3 className="mt-2 text-sm font-semibold text-gray-900">
        No payment methods
      </h3>
      <p className="mt-1 text-sm text-gray-500">
        Add a payment method to get started.
      </p>
    </div>
  );
}
